import { Injectable } from '@angular/core';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { OAuthService } from 'angular-oauth2-oidc';
import * as OktaAuth from '@okta/okta-auth-js';
import { OktaConstant } from '../../modules/okta/okta.constant';
import { HttpService } from './http/http.service';
import { OktaApiConstant } from '../constants/parmed-api.constant';
import { AppUtilService } from '../utils/app-util.service';
import { CacheConstant } from '../constants/app.constant';
import { ParmedApiService as ParmedApi } from '../api/parmed-api.service';
import { AppCacheService } from './cache/app-cache.service';
import { AppConfigService } from './init/app-config.service';

@Injectable()
export class OktaAuthService {
  /***************Constructor***************/
  constructor(
    private cache: AppCacheService,
    private http: HttpService,
    private oauthService: OAuthService,
    private api: ParmedApi,  private appConfigService: AppConfigService
  ) {}
  /***************Constructor***************/

  /***************Helper Methods***************/

  init() {
    this.oauthService.redirectUri = window.location.origin;
    this.oauthService.logoutUrl = window.location.origin;
    this.appConfigService.isConfigLoaded.subscribe(res=> {if(res){ 
      this.oauthService.clientId = this.cache.get(CacheConstant.Env_Properties).oktaClientID;
      this.oauthService.scope = 'openid email';
      this.oauthService.issuer = this.cache.get(CacheConstant.Env_Properties).oktaIssuerUrl;}});
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();

    // Load Discovery Document and then try to login the user
    this.oauthService.loadDiscoveryDocument().then(() => {
      this.oauthService.tryLoginImplicitFlow();
    });
  }

  isAuthenticated() {
    return AppUtilService.cache.get(CacheConstant.Okta_Authenticated);
  }

  getErrorMessageByCode(errorCode: string) {
    /* To go through all the code and description,
    please visit https://developer.okta.com/reference/error_codes/ */
    const errorCodeDescriptions = this.getErrorCodesWithDescription();
    return errorCodeDescriptions[errorCode] || undefined;
  }

  /***************Helper Methods***************/

  /***************Service Methods***************/

  signIn(username: string, password: string): Promise<any> {
    const authClient = new OktaAuth({
      url: this.cache.get(CacheConstant.Env_Properties).oktaIssuerUrl
    });

    return this.oauthService.createAndSaveNonce().then(nonce => {
      return authClient
        .signIn({
          username: username,
          password: password
        })
        .then(response => {
          switch (response.status) {
            case 'SUCCESS':
            case 'PASSWORD_EXPIRED':
              return response;

            case 'LOCKED_OUT':
              return Promise.reject('USER_LOCKED');

            default:
              return Promise.reject(
                'We cannot handle the ' + response.status + ' status'
              );
          }
        })
        .catch(error => {
          return error;
        });
    });
  }

  authorize(response: any) {
    const authClient = new OktaAuth({
      url: this.cache.get(CacheConstant.Env_Properties).oktaIssuerUrl
    });

    return authClient.token.getWithoutPrompt({
      clientId: this.oauthService.clientId,
      responseType: ['id_token', 'token'],
      scopes: ['openid', 'email'],
      sessionToken: response.sessionToken,
      redirectUri: window.location.origin
    });
  }

  getUserSession() {
    if (!AppUtilService.isApple()) {
      return this.http.get(
        this.cache.get(CacheConstant.Env_Properties).oktaIssuerUrl + OktaApiConstant.USER_SESSION,
        {
          headers: { ignoreLoadingBar: '' },
          withCredentials: true,
          Accept: 'application/json'
        }
      );
    } else {
      return this.validateOktaSession(
        AppUtilService.cache.get(CacheConstant.Okta_Session_Id)
      );
    }
  }

  signOut() {
    if (!AppUtilService.isApple()) {
      return this.http.delete(
        this.cache.get(CacheConstant.Env_Properties).oktaIssuerUrl + OktaApiConstant.USER_SESSION,
        {
          withCredentials: true,
          Accept: 'application/json'
        }
      );
    } else {
      return this.clearOktaSession(
        AppUtilService.cache.get(CacheConstant.Okta_Session_Id)
      );
    }
  }

  createOktaSession(sessionToken) {
    const defaultOptions = AppUtilService.getHttpDefaultOptions();
    defaultOptions.headers['Accept'] = 'application/json';

    return this.http.post(this.api.Auth.SESSION_CREATE_URL, {
      sessionToken: sessionToken,
      buId: this.cache.get(CacheConstant.Env_Properties).buId
    },defaultOptions);
  }

  validateOktaSession(sessionID) {
    const defaultOptions = AppUtilService.getHttpDefaultOptions();
    defaultOptions.headers['Accept'] = 'application/json';

    return this.http.post(
      this.api.Auth.SESSION_VALIDATE_URL, {
        sessionID: sessionID,
        buId: this.cache.get(CacheConstant.Env_Properties).buId
      },
      defaultOptions
    );
  }

  clearOktaSession(sessionID) {
    const defaultOptions = AppUtilService.getHttpDefaultOptions();
    defaultOptions.headers['Accept'] = 'application/json';

    return this.http.post(
      this.api.Auth.SESSION_CLEAR_URL, {
        sessionID: sessionID,
        buId: this.cache.get(CacheConstant.Env_Properties).buId
      },
      defaultOptions
    );
  }
  /***************Service Methods***************/

  /***************Private Methods***************/

  private getErrorCodesWithDescription() {
    return {
      E0000004: OktaConstant.Messages.InvalidUserNamePasswordMesssage,
      E0000015: OktaConstant.Messages.ParmedAccessDeniedMessage
    };
  }

  /***************Private Methods***************/
}
